// B-176 — the "?" that explains what each part of a place is for: the page on view (in the
// shell's header) or one dialog (beside its title).
//
// driver.js draws the tour: the element highlighted with the rest dimmed, a popover with a
// title, a sentence or two, "Paso N de M" and Anterior / Siguiente. The steps are the elements
// marked with `guia()` (`utils/cierreTours.js`) inside that place, read when the tour starts. The
// button pulses until this person has been through that tour once (per user, in this browser).
//
// Inside a Radix dialog the popover lives outside the dialog (driver appends it to `body`), so the
// dialog would read a click or a focus on it as "outside" and close itself, and would close on
// Escape with the tour: while a tour runs those events stop at the popover and the overlay, and
// Escape ends only the tour.
import { useEffect, useRef, useState } from "react";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import { INICIOS_DE_PAGINA, guiaVista, lugarDeGuia, marcarGuiaVista, pasosDeGuia } from "../../../utils/cierreTours";

// Who is signed in, set by the shell: the dialogs are rendered by many screens, none of which
// passes the user down, and the "seen" memory is per person.
let usuarioDeGuia = null;
export function fijarUsuarioDeGuia(usuario) {
  usuarioDeGuia = usuario || null;
}

const aislar = (el) => {
  if (!el || el.dataset.guiaAislado) return;
  el.dataset.guiaAislado = "1";
  ["pointerdown", "mousedown", "focusin"].forEach((tipo) => el.addEventListener(tipo, (e) => e.stopPropagation()));
};

/**
 * The "?" of one tour. `guiaId` names it for the "seen" memory; `titulo` and `texto` are its
 * opening step; `nombre` completes the button's label ("Guía de …").
 */
export function BotonGuia({ guiaId, titulo, texto, nombre, className }) {
  const boton = useRef(null);
  const activo = useRef(null);
  const [vista, setVista] = useState(() => guiaVista(usuarioDeGuia, guiaId));

  useEffect(() => {
    setVista(guiaVista(usuarioDeGuia, guiaId));
  }, [guiaId]);

  // Leaving the place ends its tour: its elements are gone.
  useEffect(
    () => () => {
      if (activo.current) activo.current.destroy();
      activo.current = null;
    },
    [guiaId]
  );

  const empezar = () => {
    const pasos = pasosDeGuia(lugarDeGuia(boton.current), { titulo, texto });
    // driver follows the window's scroll only; `<main>` and a dialog's body scroll on their own.
    const alDesplazar = () => {
      if (activo.current) activo.current.refresh();
    };
    const alEscape = (e) => {
      if (e.key !== "Escape" || !activo.current) return;
      e.stopPropagation();
      activo.current.destroy();
    };
    const guiaActiva = driver({
      showProgress: true,
      progressText: "Paso {{current}} de {{total}}",
      nextBtnText: "Siguiente",
      prevBtnText: "Anterior",
      doneBtnText: "Listo",
      popoverClass: "guia-cierres brinks-scope font-redhat",
      overlayOpacity: 0.55,
      stagePadding: 6,
      stageRadius: 12,
      // Instant: the pages scroll inside `<main>`, not the window, and a smooth scroll left the
      // highlight where the element was before it moved.
      smoothScroll: false,
      allowKeyboardControl: true,
      steps: pasos.map((p) => ({
        element: p.elemento || undefined,
        popover: { title: p.titulo, description: p.texto },
      })),
      onPopoverRender: (popover) => {
        aislar(popover.wrapper);
        aislar(document.querySelector(".driver-overlay"));
      },
      onDestroyed: () => {
        window.removeEventListener("keydown", alEscape, true);
        document.removeEventListener("scroll", alDesplazar, true);
        activo.current = null;
        marcarGuiaVista(usuarioDeGuia, guiaId);
        setVista(true);
      },
    });
    activo.current = guiaActiva;
    window.addEventListener("keydown", alEscape, true);
    document.addEventListener("scroll", alDesplazar, true);
    guiaActiva.drive();
  };

  return (
    <button
      ref={boton}
      type="button"
      onClick={empezar}
      aria-label={`Guía de ${nombre || titulo}`}
      title={`Guía de ${nombre || titulo}`}
      // Joined by hand, not with `cn`: the screens' tests mock @mantis-sas/ui with only what they use.
      // `relative` holds the pulse; a caller placing the button itself (`absolute …`) holds it too.
      className={[
        "grid h-10 w-10 shrink-0 place-items-center rounded-[8px] text-brinks-muted-foreground transition hover:bg-brinks-surface-hover hover:text-brinks-foreground lg:h-8 lg:w-8",
        className && /\b(absolute|fixed)\b/.test(className) ? className : ["relative", className].filter(Boolean).join(" "),
      ].join(" ")}
    >
      {/* Drawn here rather than taken from lucide: every screen's tests mock lucide with only the
          icons that screen uses, and this button is in all of them. */}
      <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="10" />
        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
        <path d="M12 17h.01" />
      </svg>
      {!vista ? (
        <span className="pointer-events-none absolute right-1.5 top-1.5 flex h-2 w-2" aria-hidden="true">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-secondary opacity-75 motion-reduce:animate-none" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-secondary" />
        </span>
      ) : null}
    </button>
  );
}

/** The header's "?": the tour of the page on view, or nothing on a page without one. */
export function GuiaPantalla({ pathname }) {
  const inicio = INICIOS_DE_PAGINA[pathname];
  if (!inicio) return null;
  return <BotonGuia key={pathname} guiaId={pathname} titulo={inicio.titulo} texto={inicio.texto} nombre={inicio.nombre} />;
}
